import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDT } from "@/lib/format";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const ENTITIES = ["user", "client", "contract", "site", "post", "guard", "leave", "shift", "roster_template", "attendance", "incident", "payroll_period", "payroll_line", "invoice", "file", "security", "calendar", "config"];

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => {
    const params = new URLSearchParams();
    if (entity !== "all") params.set("entity", entity);
    if (action) params.set("action", action);
    if (from) params.set("from_date", from);
    if (to) params.set("to_date", to);
    api.get(`/audit-log?${params}`).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [entity, action, from, to]);

  return (
    <div data-testid="audit-page">
      <PageHeader title="Audit Log" sub="Sign-ins, exports, permission changes and every schedule / attendance / incident / payroll / invoice change" />
      <div className="flex flex-wrap gap-2 mb-3 items-end">
        <div className="w-48">
          <Label className="text-[10px] uppercase font-bold">Entity</Label>
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger data-testid="audit-entity" className="rounded-none"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All entities</SelectItem>{ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] uppercase font-bold">Action contains</Label><Input data-testid="audit-action" className="rounded-none" value={action} onChange={(e) => setAction(e.target.value)} placeholder="export" /></div>
        <div><Label className="text-[10px] uppercase font-bold">From</Label><Input data-testid="audit-from" type="date" className="rounded-none" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        <div><Label className="text-[10px] uppercase font-bold">To</Label><Input data-testid="audit-to" type="date" className="rounded-none" value={to} onChange={(e) => setTo(e.target.value)} /></div>
      </div>
      <DataTable testid="audit-table" rows={rows} empty="No audit entries"
        columns={[
          { key: "created_at", label: "Time (Asia/Manila)", render: (r) => <span className="font-mono text-xs">{fmtDT(r.created_at)}</span> },
          { key: "actor", label: "Actor", render: (r) => <div><div className="text-sm font-semibold">{r.actor_name}</div><div className="font-mono text-[10px] text-muted-foreground">{r.actor_role}</div></div> },
          { key: "action", label: "Action", render: (r) => <span className="font-mono text-xs font-bold">{r.action}</span> },
          /*
            ⚠️ The id is truncated to 8 characters because a uuid is unreadable
            and nobody matches one by eye. A REFUSAL's "id" is the request PATH,
            though, and cutting that gave "/api/shi", "/api/con" and "/api/fir"
            — four different refusals rendering as the same unreadable stub, on
            the one screen whose job is to say what was refused.
          */
          { key: "entity", label: "Entity", render: (r) => {
            const isPath = String(r.entity_id || "").startsWith("/");
            return (
              <span className="font-mono text-xs">
                {r.entity}
                {r.entity_id ? ` · ${isPath ? r.entity_id : String(r.entity_id).slice(0, 8)}` : ""}
              </span>
            );
          } },
          { key: "reason", label: "Reason / meta", render: (r) => <span className="text-xs">{r.reason || (r.meta && Object.keys(r.meta).length ? JSON.stringify(r.meta).slice(0, 120) : "—")}</span> },
        ]} />
    </div>
  );
}
